import { useQuery } from "@tanstack/react-query";
import { openBalanceQueryKey } from "@/features/balance/utils/open-balance";
import { useAccessToken, useTenantData } from "@/hooks";
import { createAuthenticatedApi } from "@/lib/utils";
import { studioTodayYmd } from "@/lib/utils/date/studio-clock";
import { PaymentApi } from "@/openapi/generated";
import { ResponseError } from "@/openapi/generated/runtime";

interface UseOpenBalanceGetParams {
  userId: number;
  enabled?: boolean;
}

export const useOpenBalanceGet = ({
  userId,
  enabled = true,
}: UseOpenBalanceGetParams) => {
  const accessToken = useAccessToken();
  const { tenantId, tenantData } = useTenantData();
  const billingDateYmd = studioTodayYmd(new Date(), tenantData?.timeZone);

  return useQuery({
    queryKey: openBalanceQueryKey(userId, billingDateYmd),
    queryFn: async () => {
      if (!accessToken) throw new Error("Authentication token is required");
      const service = createAuthenticatedApi(PaymentApi, accessToken);
      try {
        const rows = await service.apiPaymentsOpenBalanceGet({
          userId,
          billingDate: billingDateYmd,
          xTenantID: tenantId,
        });
        return rows ?? [];
      } catch (error) {
        if (
          error instanceof ResponseError &&
          (error.response.status === 404 || error.response.status === 403)
        ) {
          return [];
        }
        throw error;
      }
    },
    enabled: enabled !== false && !!accessToken && !!userId && !!tenantId,
    staleTime: 2 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};
